import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../../auth/AuthContext'
import type { WalletBalance } from '../../types/wallet'
import { DECIMALS, MINTS, SYMBOLS, jupiterQuote, jupiterSwap, type JupiterQuote } from '../../api/trade'
import { deserializeV0 } from '../../lib/tx'
import { useAdapterSignAndSubmit } from '../../lib/walletAdapter'
import { findBalance, formatBalance, formatUsdShort } from '../../hooks/useWalletBalance'
import TokenIcon from '../common/TokenIcon'
import TradeResultBanner, { type TradeResult } from './TradeResultBanner'

const MINT_OPTIONS = [MINTS.SOL, MINTS.USDC, MINTS.jupSOL]
// Cushion = priority fee (Jupiter swap is high-CU) + one fresh ATA rent
// (~0.00204 SOL) when the destination token account doesn't exist yet. Bumped
// from 0.005 → 0.01 because at 0.005 the chain still threw InsufficientFunds
// when MAX was clicked on a SOL→jupSOL route with no jupSOL ATA.
const SOL_GAS_CUSHION  = 0.01
const MIN_SOL_FOR_SWAP = 0.01

function parseAmount(raw: string, decimals: number): bigint | null {
  if (!raw || !/^[0-9]*\.?[0-9]*$/.test(raw)) return null
  const [whole, frac = ''] = raw.split('.')
  const padded = (frac + '0'.repeat(decimals)).slice(0, decimals)
  try { return BigInt((whole || '0') + padded) || null } catch { return null }
}

function formatUnits(raw: string | number | undefined | null, decimals: number): string {
  if (raw == null) return '—'
  try {
    const n = typeof raw === 'string' ? BigInt(raw) : BigInt(Math.floor(Number(raw)))
    const s = n.toString().padStart(decimals + 1, '0')
    const head = s.slice(0, -decimals)
    const tail = s.slice(-decimals).replace(/0+$/, '')
    return tail ? `${head}.${tail}` : head
  } catch { return String(raw) }
}

interface Props {
  balances: WalletBalance[] | null
  defaultAmount?: string
  defaultInputMint?: string
  defaultOutputMint?: string
  onSuccess?: () => void
}

export default function SwapForm({ balances, defaultAmount, defaultInputMint, defaultOutputMint, onSuccess }: Props) {
  const { portfolioId } = useAuth()
  const { signAndSubmit, publicKey, connected, connect, wallet, friendlyTxError } = useAdapterSignAndSubmit()

  const [inputMint, setInputMint]   = useState<string>(defaultInputMint ?? MINTS.SOL)
  const [outputMint, setOutputMint] = useState<string>(defaultOutputMint ?? MINTS.USDC)
  const [amount, setAmount]         = useState(defaultAmount ?? '')
  const [slippageBps, setSlippage]  = useState(50)
  const [quote, setQuote]           = useState<JupiterQuote | null>(null)
  const [quoting, setQuoting]       = useState(false)
  const [result, setResult]         = useState<TradeResult>({ kind: 'idle' })

  const rawInput = useMemo(() => parseAmount(amount, DECIMALS[inputMint]), [amount, inputMint])
  const inBalance = findBalance(balances, inputMint)
  // Native-SOL balance — separate from inBalance because the user may swap a
  // non-SOL asset (USDC → jupSOL) yet still need SOL for fees + ATA rent.
  const solBalanceNum = parseFloat(findBalance(balances, MINTS.SOL)?.amount ?? '0')
  const insufficientSolForFees = balances != null && solBalanceNum < MIN_SOL_FOR_SWAP

  const fetchQuote = useCallback(async () => {
    if (!rawInput || rawInput <= 0n || inputMint === outputMint) { setQuote(null); return }
    setQuoting(true)
    try {
      const q = await jupiterQuote({ inputMint, outputMint, amount: rawInput.toString(), slippageBps })
      setQuote(q)
    } catch (e) {
      setQuote(null)
      setResult({ kind: 'error', message: friendlyTxError(e) })
    } finally { setQuoting(false) }
  }, [rawInput, inputMint, outputMint, slippageBps, friendlyTxError])

  useEffect(() => {
    const h = setTimeout(() => { fetchQuote() }, 400)
    return () => clearTimeout(h)
  }, [fetchQuote])

  const swap = async () => {
    if (!quote) return
    setResult({ kind: 'pending', label: 'Building swap transaction…' })
    try {
      // Bridge connect is async — attempt a silent connect if it hasn't resolved yet.
      if ((!connected || !publicKey) && wallet) {
        try { await connect() } catch { /* fall through; signAndSubmit will throw if truly not connected */ }
      }
      // Use the auth-verified wallet address as signer so we don't depend on the
      // React-state publicKey, which can be stale immediately after connect().
      const signer = portfolioId
        ?? wallet?.adapter.publicKey?.toBase58()
        ?? publicKey?.toBase58()
      if (!signer) throw new Error('Wallet not connected — open Phantom/Solflare and retry')
      const { swapTransaction, lastValidBlockHeight } = await jupiterSwap({ userPublicKey: signer, quoteResponse: quote })
      setResult({ kind: 'pending', label: 'Waiting for wallet signature…' })
      const tx = deserializeV0(swapTransaction)
      const submitted = await signAndSubmit(tx, { lastValidBlockHeight })
      const inReadable  = formatUnits(quote.inAmount,  DECIMALS[inputMint])
      const outReadable = formatUnits(quote.outAmount, DECIMALS[outputMint])
      setResult({
        kind: 'success',
        signature: submitted.signature,
        summary: `Swapped ${inReadable} ${SYMBOLS[inputMint]} → ${outReadable} ${SYMBOLS[outputMint]}`,
      })
      setAmount('')
      setQuote(null)
      // Give the RPC node ~1.5 s to reflect the confirmed state, then notify the
      // parent so it can re-fetch wallet balances without waiting for the next
      // scheduled poll cycle.
      if (onSuccess) setTimeout(onSuccess, 1500)
    } catch (e) {
      setResult({ kind: 'error', message: friendlyTxError(e) })
    }
  }

  const onMax = () => {
    if (!inBalance) return
    let max = parseFloat(inBalance.amount)
    if (inputMint === MINTS.SOL) max = Math.max(0, max - SOL_GAS_CUSHION)
    setAmount(max.toString())
  }

  const outReadable = quote ? formatUnits(quote.outAmount, DECIMALS[outputMint]) : '—'
  const priceImpact = quote?.priceImpactPct ? (Number(quote.priceImpactPct) * 100).toFixed(2) + '%' : '—'

  return (
    <div className="space-y-3">
      <MintField
        label="You pay"
        mint={inputMint}
        onMintChange={setInputMint}
        amount={amount}
        onAmountChange={setAmount}
        balances={balances}
        onMax={onMax}
        editable
      />
      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => { setInputMint(outputMint); setOutputMint(inputMint); setAmount('') }}
          className="h-7 w-7 rounded-full border border-fringe-strong bg-surface-hover text-ink-muted hover:text-ink"
          aria-label="Swap direction"
        >↓</button>
      </div>
      <MintField
        label="You receive"
        mint={outputMint}
        onMintChange={setOutputMint}
        amount={outReadable}
        balances={balances}
        readonly
      />

      <div className="flex items-center justify-between text-[11px] text-gray-500">
        <span>Slippage</span>
        <div className="flex items-center gap-1">
          {[25, 50, 100].map(v => (
            <button key={v}
              onClick={() => setSlippage(v)}
              className={`rounded-md px-2 py-1 ${slippageBps === v ? 'bg-accent-purple/25 text-ink' : 'text-ink-muted hover:bg-surface-hover'}`}
            >{(v / 100).toFixed(2)}%</button>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between text-[11px] text-gray-500">
        <span>Price impact</span>
        <span className="text-ink">{priceImpact}</span>
      </div>

      {insufficientSolForFees && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200 leading-relaxed">
          Wallet has only {solBalanceNum.toFixed(4)} SOL — every swap needs at least
          {' '}{MIN_SOL_FOR_SWAP} SOL on hand to pay tx fees and rent for the
          destination token account. Top up your wallet with SOL and retry.
        </div>
      )}

      <button
        type="button"
        disabled={!quote || result.kind === 'pending' || insufficientSolForFees}
        onClick={swap}
        className="w-full rounded-lg bg-accent-purple py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-accent-purple/40"
      >
        {quoting ? 'Quoting…'
          : result.kind === 'pending' ? 'Working…'
          : insufficientSolForFees ? 'Top up SOL to swap'
          : connected ? 'Sign & Swap' : 'Connect & Swap'}
      </button>

      <TradeResultBanner result={result} onDismiss={() => setResult({ kind: 'idle' })} />
    </div>
  )
}

function MintField({
  label, mint, onMintChange, amount, onAmountChange, balances, onMax, editable = false, readonly = false,
}: {
  label: string
  mint: string
  onMintChange: (m: string) => void
  amount: string
  onAmountChange?: (v: string) => void
  balances: WalletBalance[] | null
  onMax?: () => void
  editable?: boolean
  readonly?: boolean
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const balance = findBalance(balances, mint)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  return (
    <div ref={ref} className="relative rounded-lg border border-fringe bg-surface-hover p-3">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-gray-500">{label}</span>
        {balance && (
          <span className="text-[10px] text-gray-500">
            Balance: <span className="text-ink">{formatBalance(balance.amount)}</span>
            {' · '}
            <span className="text-ink-muted">{formatUsdShort(balance.usdValue)}</span>
            {editable && onMax && (
              <button type="button" onClick={onMax} className="ml-2 text-accent-purple hover:underline">MAX</button>
            )}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1.5 rounded-md bg-surface-card px-2 py-1.5 text-xs font-semibold text-ink hover:bg-surface-hover"
        >
          <TokenIcon symbol={SYMBOLS[mint] ?? '?'} mint={mint} size={16} />
          <span>{SYMBOLS[mint] ?? '?'}</span>
          <svg viewBox="0 0 10 6" className="h-2 w-2 fill-none stroke-current" strokeWidth="1.5"><polyline points="1,1 5,5 9,1"/></svg>
        </button>
        <input
          type="text"
          inputMode="decimal"
          placeholder="0.0"
          value={amount}
          readOnly={readonly || !editable}
          onChange={e => onAmountChange?.(e.target.value)}
          className="flex-1 bg-transparent text-right text-lg font-semibold text-ink outline-none"
        />
      </div>

      {open && (
        <div className="absolute left-3 right-3 top-[calc(100%-4px)] z-20 mt-1 overflow-hidden rounded-lg border border-fringe-strong bg-surface-card shadow-xl">
          {MINT_OPTIONS.map(m => {
            const b = findBalance(balances, m)
            return (
              <button key={m}
                type="button"
                onClick={() => { onMintChange(m); setOpen(false) }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors hover:bg-surface-hover
                  ${m === mint ? 'bg-accent-purple/10' : ''}`}
              >
                <TokenIcon symbol={SYMBOLS[m] ?? '?'} mint={m} size={18} />
                <span className="font-semibold text-ink">{SYMBOLS[m]}</span>
                <span className="ml-auto text-right text-gray-500">
                  {b ? (<><span className="text-ink">{formatBalance(b.amount)}</span>{' · '}{formatUsdShort(b.usdValue)}</>) : '—'}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
